import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { api, getAppLock, setAppLock, getFontSize, setFontSize, mediaSrcById } from "../api";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { FixedHelpersEditor } from "../components/FixedHelpersEditor";
import { toast } from "sonner";
import { playVoice } from "../lib/sounds";
import { buddyEnabled } from "../lib/buddyPrefs";
import { resizeToJpeg } from "../lib/imageResize";
import { getOfflineSettings, usage, fmtBytes } from "../lib/offlineSync";
import { getTheme, setTheme } from "../lib/theme";
import { BuddyMemoriesCard } from "../components/BuddyMemoriesCard";
import { BuddyRemindersCard } from "../components/BuddyRemindersCard";
import { OfflineSettingsCard } from "../components/OfflineSettingsCard";
import {
  ArrowLeft, ChevronRight, Bell, Fingerprint, Type, Image as ImageIcon, Globe, Languages, Bot, Sparkles, Brain, AlarmClock, CloudOff, PlayCircle,
  Sun, Moon, MonitorSmartphone,
  Users,
} from "lucide-react";

const BUDDY_VOICE_IDS = ["echo", "alloy", "fable", "onyx", "nova", "shimmer"];

/* ---- building blocks ---- */
const Section = ({ title, children, testid }) => (
  <section className="mb-7" data-testid={testid}>
    <h2 className="text-[11px] font-semibold tracking-[0.12em] uppercase text-muted-foreground px-1 mb-2">{title}</h2>
    <div className="rounded-2xl border bg-card divide-y overflow-hidden shadow-sm">{children}</div>
  </section>
);

const Row = ({ icon: Icon, title, subtitle, right, onClick, testid, children }) => {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} data-testid={testid}
      className={`w-full min-w-0 flex items-center gap-3.5 px-4 py-3.5 text-left ${onClick ? "hover:bg-muted/50 active:bg-muted transition-colors" : ""}`}>
      <span className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon className="h-[18px] w-[18px]" /></span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium leading-tight">{title}</span>
        {subtitle && <span className="block text-xs text-muted-foreground mt-0.5 truncate">{subtitle}</span>}
        {children}
      </span>
      {right}
      {onClick && <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
    </Tag>
  );
};

const SubHeader = ({ title, onBack, t }) => (
  <div className="flex items-center gap-2 mb-5">
    <Button variant="ghost" size="icon" className="h-9 w-9" onClick={onBack} data-testid="settings-sub-back" aria-label={t("back")}><ArrowLeft className="h-5 w-5" /></Button>
    <h1 className="font-display text-xl font-semibold">{title}</h1>
  </div>
);

export default function SettingsPage() {
  const { lang, setLang, t, user, setUser } = useApp();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const sub = params.get("s"); // voice | memories | reminders | wallpaper | offline
  const openSub = (s) => setParams(s ? { s } : {});

  const [notifOn, setNotifOn] = useState(() => localStorage.getItem("pmf_notifications") !== "off");
  const [appLock, setAppLockState] = useState(getAppLock);
  const [fontSize, setFontSizeState] = useState(getFontSize);
  const [theme, setThemeState] = useState(getTheme);
  const changeTheme = (m) => { setTheme(m); setThemeState(m); };
  useEffect(() => {
    const fn = () => { setNotifOn(localStorage.getItem("pmf_notifications") !== "off"); setThemeState(getTheme()); setFontSizeState(getFontSize()); };
    window.addEventListener("osc-settings-changed", fn);
    return () => window.removeEventListener("osc-settings-changed", fn);
  }, []);
  const wpFileRef = useRef(null);
  const [wpBusy, setWpBusy] = useState(false);
  const [buddyVoice, setBuddyVoice] = useState(user?.buddy_voice || "echo");
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [offUse, setOffUse] = useState(null);
  useEffect(() => { usage().then(setOffUse).catch(() => {}); }, [sub]);

  const patchUser = async (patch, errKey = "something_wrong") => {
    try { await api.patch(`/users/${user.id}`, patch); setUser({ ...user, ...patch }); return true; }
    catch (e) { toast.error(t(errKey)); return false; }
  };
  const pickVoice = async (v) => { setBuddyVoice(v); await patchUser({ buddy_voice: v }, "buddy_voice_error"); };
  const previewVoice = async () => {
    if (voiceBusy) return;
    setVoiceBusy(true);
    try {
      const { data } = await api.get(`/buddy/voice-preview?voice=${buddyVoice}`);
      if (data?.audio_base64) { const h = await playVoice(data.audio_base64, { onEnd: () => setVoiceBusy(false) }); if (h) return; }
      setVoiceBusy(false);
    } catch (e) { toast.error(t("buddy_voice_error")); setVoiceBusy(false); }
  };
  const onPickWallpaper = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setWpBusy(true);
    try {
      const b64 = await resizeToJpeg(file, 1440, 0.8);
      const up = await api.post("/media", { data_base64: b64, content_type: "image/jpeg", kind: "image", category: "wallpaper", uploaded_by: user.id, filename: "wallpaper.jpg" });
      if (await patchUser({ wallpaper_media_id: up.data.id })) toast.success(t("wallpaper_updated"));
    } catch (err) { toast.error(t("something_wrong")); }
    finally { setWpBusy(false); }
  };
  const resetWallpaper = async () => {
    setWpBusy(true);
    if (await patchUser({ wallpaper_media_id: "" })) { setUser({ ...user, wallpaper_media_id: null }); toast.success(t("wallpaper_reset")); }
    setWpBusy(false);
  };
  const changeFontSize = (s) => { setFontSize(s); setFontSizeState(s); };
  const toggleAppLock = (c) => { setAppLock(c); setAppLockState(c); toast.success(c ? t("app_lock_enabled") : t("app_lock_disabled")); };
  const toggleNotifications = async (checked) => {
    setNotifOn(checked);
    if (checked) {
      localStorage.setItem("pmf_notifications", "on");
      if ("Notification" in window && Notification.permission !== "granted") {
        const r = await Notification.requestPermission();
        if (r !== "granted") toast.error(t("notifications_blocked")); else toast.success(t("notifications_enabled"));
      } else toast.success(t("notifications_enabled"));
    } else { localStorage.setItem("pmf_notifications", "off"); toast.info(t("notifications_off")); }
  };

  const offSettings = getOfflineSettings(user.id);
  const offSummary = offSettings?.enabled
    ? (offUse ? `${t("offline_mode_on_short")} · ${offUse.conversations} ${t("conversations").toLowerCase()} · ${fmtBytes(offUse.mediaBytes)}` : t("offline_mode_on_short"))
    : t("offline_mode_off_short");
  const langName = lang === "en" ? "English" : lang === "es" ? "Español" : "Português";

  /* ---- sub-screens ---- */
  const subScreens = {
    voice: (
      <>
        <SubHeader title={t("buddy_voice_title")} onBack={() => openSub(null)} t={t} />
        <p className="text-sm text-muted-foreground mb-4">{t("buddy_voice_desc")}</p>
        <div className="grid grid-cols-2 gap-2">
          {BUDDY_VOICE_IDS.map((v) => (
            <Button key={v} variant={buddyVoice === v ? "default" : "outline"} className="h-11 justify-start px-3" data-testid={`settings-voice-${v}`} onClick={() => pickVoice(v)}>
              <span className="text-sm truncate capitalize">{v} · {t(`voice_${v}`)}</span>
            </Button>
          ))}
        </div>
        <Button variant="secondary" className="h-10 mt-4 w-full" data-testid="settings-voice-preview" disabled={voiceBusy} onClick={previewVoice}>
          <PlayCircle className="h-4 w-4 mr-1.5" /> {voiceBusy ? "..." : t("buddy_voice_preview")}
        </Button>
      </>
    ),
    memories: (<><SubHeader title={t("buddy_mem_title")} onBack={() => openSub(null)} t={t} /><BuddyMemoriesCard user={user} t={t} defaultOpen /></>),
    reminders: (<><SubHeader title={t("buddy_rem_title")} onBack={() => openSub(null)} t={t} /><BuddyRemindersCard user={user} t={t} defaultOpen /></>),
    offline: (<><SubHeader title={t("offline_mode")} onBack={() => openSub(null)} t={t} /><OfflineSettingsCard user={user} t={t} /></>),
    wallpaper: (
      <>
        <SubHeader title={t("wallpaper_title")} onBack={() => openSub(null)} t={t} />
        <div className="rounded-2xl border overflow-hidden mb-4 h-56 bg-muted" data-testid="settings-wallpaper-preview">
          <img src={user?.wallpaper_media_id ? mediaSrcById(user.wallpaper_media_id) : "/chat-bg.jpg"} alt="" className="h-full w-full object-cover" />
        </div>
        <p className="text-sm text-muted-foreground mb-3">{user?.wallpaper_media_id ? t("wallpaper_custom") : t("wallpaper_default")}</p>
        <input ref={wpFileRef} type="file" accept="image/*" className="hidden" onChange={onPickWallpaper} />
        <div className="grid grid-cols-2 gap-2">
          <Button className="h-10" disabled={wpBusy} onClick={() => wpFileRef.current?.click()} data-testid="settings-wallpaper-choose">{t("wallpaper_choose")}</Button>
          <Button variant="outline" className="h-10" disabled={wpBusy || !user?.wallpaper_media_id} onClick={resetWallpaper} data-testid="settings-wallpaper-reset">{t("wallpaper_default")}</Button>
        </div>
      </>
    ),
  };

  return (
    <div className="w-full max-w-xl mx-auto px-4 pt-4 pb-28 min-w-0 overflow-x-hidden" data-testid="settings-page">
      {sub && subScreens[sub] ? subScreens[sub] : (
        <>
          <div className="flex items-center gap-2 mb-6">
            <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => navigate("/more")} data-testid="settings-back" aria-label={t("back")}><ArrowLeft className="h-5 w-5" /></Button>
            <h1 className="font-display text-2xl font-semibold">{t("settings")}</h1>
          </div>

          <Section title={t("settings_sec_account")} testid="settings-sec-account">
            <Row icon={Fingerprint} title={t("app_lock_title")} subtitle={appLock ? t("app_lock_on_desc") : t("app_lock_off_desc")} testid="settings-applock-row"
              right={<Switch data-testid="settings-applock-switch" checked={appLock} onCheckedChange={toggleAppLock} />} />
            <Row icon={Bell} title={t("notifications")} subtitle={notifOn ? t("notifications_enabled") : t("notifications_off")} testid="settings-notifications-row"
              right={<Switch data-testid="settings-notifications-switch" checked={notifOn} onCheckedChange={toggleNotifications} />} />
          </Section>

          <Section title={t("settings_sec_appearance")} testid="settings-sec-appearance">
            <Row icon={Type} title={t("font_size_title")} subtitle={t(`font_${fontSize}`)} testid="settings-fontsize-row"
              right={<div className="flex gap-1">{["small", "medium", "large"].map((s) => (
                <Button key={s} size="sm" variant={fontSize === s ? "default" : "outline"} data-testid={`settings-font-${s}`} className="h-8 w-9 px-0" onClick={() => changeFontSize(s)}>
                  <span className={s === "small" ? "text-xs" : s === "large" ? "text-base" : "text-sm"}>A</span>
                </Button>))}</div>} />
            <Row icon={theme === "dark" ? Moon : theme === "system" ? MonitorSmartphone : Sun} title={t("theme_title")} subtitle={t(`theme_${theme}`)} testid="settings-theme-row"
              right={<div className="flex gap-1">{[["light", Sun], ["dark", Moon], ["system", MonitorSmartphone]].map(([m, I]) => (
                <Button key={m} size="sm" variant={theme === m ? "default" : "outline"} data-testid={`settings-theme-${m}`} className="h-8 w-9 px-0" aria-label={t(`theme_${m}`)} onClick={() => changeTheme(m)}><I className="h-4 w-4" /></Button>))}</div>} />
            <Row icon={ImageIcon} title={t("wallpaper_title")} subtitle={user?.wallpaper_media_id ? t("wallpaper_custom") : t("wallpaper_default")} onClick={() => openSub("wallpaper")} testid="settings-wallpaper-row"
              right={<span className="block h-10 w-7 rounded-md border overflow-hidden shrink-0 bg-muted"><img src={user?.wallpaper_media_id ? mediaSrcById(user.wallpaper_media_id) : "/chat-bg.jpg"} alt="" className="h-full w-full object-cover" /></span>} />
            <Row icon={Globe} title={t("language")} subtitle={langName} testid="settings-language-row"
              right={<div className="flex gap-1">{["en", "pt", "es"].map((l) => (
                <Button key={l} size="sm" variant={lang === l ? "default" : "outline"} data-testid={`settings-lang-${l}`} className="h-8 px-2.5 text-xs" onClick={() => setLang(l)}>{l.toUpperCase()}</Button>))}</div>} />
          </Section>

          <Section title={t("settings_sec_buddy")} testid="settings-sec-buddy">
            <Row icon={Bot} title={t("buddy_enabled_title")} subtitle={buddyEnabled(user) ? t("buddy_enabled_on") : t("buddy_enabled_off")} testid="settings-buddy-row"
              right={<Switch checked={buddyEnabled(user)} onCheckedChange={(v) => patchUser({ buddy_enabled: !!v })} data-testid="settings-buddy-switch" />} />
            {buddyEnabled(user) && (
              <>
                <Row icon={Sparkles} title={t("buddy_voice_title")} subtitle={`${buddyVoice[0].toUpperCase()}${buddyVoice.slice(1)} · ${t(`voice_${buddyVoice}`)}`} onClick={() => openSub("voice")} testid="settings-buddy-voice-row" />
                <Row icon={Brain} title={t("buddy_mem_title")} subtitle={t("buddy_memories_short")} onClick={() => openSub("memories")} testid="settings-buddy-memories-row" />
                <Row icon={AlarmClock} title={t("buddy_rem_title")} subtitle={t("buddy_reminders_short")} onClick={() => openSub("reminders")} testid="settings-buddy-reminders-row" />
                <Row icon={Users} title={t("interp_ask_helpers_label")} subtitle={t("interp_ask_helpers_user_hint")} testid="settings-ask-helpers-row"
                  right={<Switch checked={user?.interp_ask_helpers === true} onCheckedChange={(v) => patchUser({ interp_ask_helpers: !!v })} data-testid="settings-ask-helpers-switch" />} />
                <FixedHelpersEditor />
              </>
            )}
          </Section>

          <Section title={t("settings_sec_data")} testid="settings-sec-data">
            <Row icon={CloudOff} title={t("offline_mode")} subtitle={offSummary} onClick={() => openSub("offline")} testid="settings-offline-row" />
            <Row icon={Languages} title={t("ai_settings_title")} subtitle={user?.ai_consent === true ? t("ai_settings_on") : t("ai_settings_off")} testid="settings-ai-row"
              right={<Switch checked={user?.ai_consent === true} onCheckedChange={(v) => patchUser({ ai_consent: !!v }, "ai_consent_error")} data-testid="settings-ai-switch" />} />
          </Section>
        </>
      )}
    </div>
  );
}
